/* ==========================================================================
   useAuth Hook
   Convenience hook for authentication with simplified API
   ========================================================================== */

import { useAuthContext } from '@/context/AuthContext'

/**
 * Hook to access auth state and methods.
 * Provides the same interface as useAuthContext with better semantics.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, isAuthenticated, login, logout } = useAuth()
 *
 *   if (!isAuthenticated) {
 *     return <LoginPrompt />
 *   }
 *
 *   return <div>Welcome, {user?.name}</div>
 * }
 * ```
 */
export function useAuth() {
  return useAuthContext()
}

/**
 * Check if the current user's profile is complete.
 * A complete profile has all required fields filled:
 * - name
 * - npi
 * - license_number
 * - license_state
 *
 * @returns true if profile is complete, false otherwise
 */
export function useProfileComplete(): boolean {
  const { user } = useAuthContext()
  return user?.profile_complete ?? false
}

/**
 * Get the current user's profile if authenticated.
 * Returns null if not authenticated.
 */
export function useUser() {
  const { user } = useAuthContext()
  return user
}

/**
 * Check if the user is currently authenticated.
 */
export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuthContext()
  return isAuthenticated
}

export default useAuth
